import { ReactNode } from 'react';
import React, { useEffect, useState, useRef } from 'react';
import { Box } from '@osmosis/react-ui/components/Box';
import { BoxProps } from '@osmosis/react-ui/components/Box';
import { Flex } from '@osmosis/react-ui/components/Box';
import Mrkr from 'mrkrjs';
import style from './types/enhanced-hot-spot/EnhancedHotSpot.module.css';
import { QuizSettingsPopup } from './quizSettingsPopup/QuizSettingsPopup';
import { useQuizContext } from '../../context/QuizContext';
import { PopupMenuItem } from '../PopupMenu';

type Props = BoxProps & {
  headerItems?: ReactNode;
  settingsHeading?: string;
  settingItems?: PopupMenuItem[];
};

/**
 * QuizContainer.
 */
export const QuizContainer = ({
  children,
  settingItems = [],
  settingsHeading,
  headerItems,
  ...rest
}: Props) => {
  const { fullQuizSize, highlighting, currentItem } = useQuizContext();
  const [marker, setMarker] = useState<Mrkr | null>(null);
  const highlightData = useRef<
    { endOffset?: number; startOffset?: number }[] | null
  >(null);

  useEffect(() => {
    if (marker == null) {
      const container = document.getElementsByClassName(
        'main-quiz-container',
      )[0];
      const mark = new Mrkr(container as HTMLElement, style['user-highlight']);
      setMarker(mark);
      mark?.disableSelection();
    }
  }, [marker]);

  useEffect(() => {
    if (highlightData.current != null) {
      highlightData.current.forEach((data) => {
        marker?.highlightRange(data.startOffset ?? 0, data.endOffset ?? 0);
      });
    }
  });

  // Enabled/disable marker depending on current item and highlight settings.
  useEffect(() => {
    marker?.disableSelection();
    const isEnhancedHotspot = currentItem?.subtype === 11;
    highlighting && !isEnhancedHotspot
      ? marker?.enableSelection()
      : (() => {
          marker?.disableSelection();
          marker?.clear();
        })();
  }, [highlighting, marker, currentItem]);

  const saveHighlightData = () => {
    const data = marker?.getData();
    if (data?.length) {
      highlightData.current = data;
    }
  };

  return (
    <Flex
      className="main-quiz-container"
      onPointerUp={saveHighlightData}
      height="auto"
      justifySelf="center"
      {...rest}
      sx={{
        position: 'relative',
        boxSizing: 'border-box',
        borderRadius: 5,
        boxShadow: 'large',
        p: 2,
        flexDirection: 'column',
        maxWidth: fullQuizSize ? undefined : '900px',
        textAlign: 'center',
        width: '100%',
        ...rest?.sx,
      }}
    >
      <Flex sx={{ gap: 2 }} justifyContent="flex-end">
        {headerItems}
        <QuizSettingsPopup heading={settingsHeading} items={settingItems} />
      </Flex>
      <Box tabIndex={0} data-testid="quiz-question" p={3}>
        {children}
      </Box>
    </Flex>
  );
};
